import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import {
  fetchSkillsAdmin,
  fetchSkillCategories,
  updateSkill,
  deleteSkill,
  mergeSkills,
} from '../api/skillAdmin';
import type { SkillWithStats } from '../api/skillAdmin';

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export default function SkillAdmin() {
  const { t } = useTranslation();

  // Table state
  const [skills, setSkills] = useState<SkillWithStats[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [category, setCategory] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Categories for filter dropdown
  const [categories, setCategories] = useState<string[]>([]);

  // Selection state for merge
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Inline edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState('');

  // Merge modal state
  const [showMerge, setShowMerge] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState<string | null>(null);
  const [merging, setMerging] = useState(false);

  // Success message
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Debounce search input
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  // Load categories once
  useEffect(() => {
    fetchSkillCategories()
      .then(setCategories)
      .catch(() => {});
  }, []);

  // Load skills
  const loadSkills = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchSkillsAdmin({
        page,
        page_size: pageSize,
        search: debouncedSearch || undefined,
        category: category || undefined,
        sort_by: sortBy,
        order,
      });
      setSkills(res.items);
      setTotal(res.total);
      setTotalPages(res.total_pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('skillAdmin.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, category, sortBy, order, t]);

  useEffect(() => {
    loadSkills();
  }, [loadSkills]);

  // Clear success message after a delay
  useEffect(() => {
    if (!successMsg) return;
    const timer = setTimeout(() => setSuccessMsg(null), 4000);
    return () => clearTimeout(timer);
  }, [successMsg]);

  // Category options for react-select
  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: c, label: c })),
    [categories],
  );

  // Sort handler
  const handleSort = (field: string) => {
    if (sortBy === field) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setOrder('asc');
    }
    setPage(1);
  };

  const sortIndicator = (field: string) => {
    if (sortBy !== field) return '';
    return order === 'asc' ? ' \u2191' : ' \u2193';
  };

  // Selection handlers
  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === skills.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(skills.map((s) => s.id)));
    }
  };

  // Inline edit handlers
  const startEdit = (skill: SkillWithStats) => {
    setEditingId(skill.id);
    setEditName(skill.name);
    setEditCategory(skill.category ?? '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditCategory('');
  };

  const saveEdit = async () => {
    if (!editingId || !editName.trim()) return;
    try {
      await updateSkill(editingId, {
        name: editName.trim(),
        category: editCategory.trim() || null,
      });
      cancelEdit();
      await loadSkills();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update skill');
    }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  // Delete handler
  const handleDelete = async (skill: SkillWithStats) => {
    if (skill.total_references > 0) {
      setError(
        t('skillAdmin.deleteHasRefs', { count: skill.total_references }),
      );
      return;
    }
    if (!window.confirm(t('skillAdmin.deleteConfirm'))) return;
    try {
      await deleteSkill(skill.id);
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(skill.id);
        return next;
      });
      await loadSkills();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete skill');
    }
  };

  // Merge handlers
  const openMerge = () => {
    setShowMerge(true);
    setMergeTargetId(null);
  };

  const closeMerge = () => {
    setShowMerge(false);
    setMergeTargetId(null);
  };

  const selectedSkills = useMemo(
    () => skills.filter((s) => selected.has(s.id)),
    [skills, selected],
  );

  const mergeTargetOptions = useMemo(
    () => selectedSkills.map((s) => ({ value: s.id, label: s.name })),
    [selectedSkills],
  );

  const handleMerge = async () => {
    if (!mergeTargetId || selected.size < 2) return;
    setMerging(true);
    setError(null);
    try {
      const sourceIds = [...selected].filter((id) => id !== mergeTargetId);
      await mergeSkills(mergeTargetId, sourceIds);
      setSelected(new Set());
      closeMerge();
      setSuccessMsg(t('skillAdmin.mergeSuccess'));
      await loadSkills();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  // Ref count badge
  const refBadge = (count: number) => (
    <span
      className={[
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        count > 0
          ? 'bg-blue-100 text-blue-700'
          : 'bg-gray-100 text-gray-500',
      ].join(' ')}
    >
      {count}
    </span>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {t('skillAdmin.title')}
        </h1>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('skillAdmin.searchPlaceholder')}
            className="block w-full rounded-md border-gray-300 text-sm sm:w-64"
            aria-label={t('skillAdmin.searchPlaceholder')}
          />
          <div className="w-full sm:w-56">
            <Select
              options={categoryOptions}
              value={categoryOptions.find((o) => o.value === category) ?? null}
              onChange={(opt) => {
                setCategory(opt?.value ?? '');
                setPage(1);
              }}
              placeholder={t('skillAdmin.allCategories')}
              isClearable
              classNamePrefix="react-select"
              aria-label={t('skillAdmin.allCategories')}
              styles={{
                control: (base) => ({ ...base, fontSize: '0.875rem', minHeight: '38px' }),
                option: (base) => ({ ...base, fontSize: '0.875rem' }),
              }}
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">
            {t('skillAdmin.skillCount', { count: total })}
          </span>
          {selected.size >= 2 && (
            <button
              type="button"
              onClick={openMerge}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
            >
              {t('skillAdmin.mergeSelected')} ({t('skillAdmin.selected', { count: selected.size })})
            </button>
          )}
        </div>
      </div>

      {/* Success message */}
      {successMsg && (
        <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-700" role="status">
          {successMsg}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700" role="alert">
          {error}
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700 font-medium"
            aria-label={t('common.close')}
          >
            x
          </button>
        </div>
      )}

      {/* Merge modal */}
      {showMerge && (
        <div className="rounded-lg border border-brand-200 bg-brand-50 p-4 space-y-3">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('skillAdmin.mergeTitle')}
          </h2>
          <p className="text-sm text-gray-600">
            {t('skillAdmin.mergeTarget')}
          </p>
          <div className="w-full sm:w-72">
            <Select
              options={mergeTargetOptions}
              value={mergeTargetOptions.find((o) => o.value === mergeTargetId) ?? null}
              onChange={(opt) => setMergeTargetId(opt?.value ?? null)}
              classNamePrefix="react-select"
              placeholder={t('common.select')}
              styles={{
                control: (base) => ({ ...base, fontSize: '0.875rem', minHeight: '38px' }),
                option: (base) => ({ ...base, fontSize: '0.875rem' }),
              }}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleMerge}
              disabled={!mergeTargetId || merging}
              className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {merging ? t('common.loading') : t('skillAdmin.mergeConfirm')}
            </button>
            <button
              type="button"
              onClick={closeMerge}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12" role="status">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-brand-500" />
          <span className="sr-only">{t('skillAdmin.loading')}</span>
        </div>
      ) : skills.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center text-sm text-gray-500">
          {t('skillAdmin.noSkills')}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selected.size === skills.length && skills.length > 0}
                    onChange={toggleSelectAll}
                    aria-label="Select all skills"
                    className="rounded border-gray-300"
                  />
                </th>
                <th
                  className="px-4 py-3 text-left font-medium text-gray-500 cursor-pointer select-none"
                  onClick={() => handleSort('name')}
                >
                  {t('skillAdmin.nameCol')}{sortIndicator('name')}
                </th>
                <th
                  className="px-4 py-3 text-left font-medium text-gray-500 cursor-pointer select-none"
                  onClick={() => handleSort('category')}
                >
                  {t('skillAdmin.categoryCol')}{sortIndicator('category')}
                </th>
                <th className="px-4 py-3 text-center font-medium text-gray-500">
                  {t('skillAdmin.employeesCol')}
                </th>
                <th className="px-4 py-3 text-center font-medium text-gray-500">
                  {t('skillAdmin.projectsCol')}
                </th>
                <th className="px-4 py-3 text-center font-medium text-gray-500">
                  {t('skillAdmin.tendersCol')}
                </th>
                <th
                  className="px-4 py-3 text-center font-medium text-gray-500 cursor-pointer select-none"
                  onClick={() => handleSort('total_references')}
                >
                  {t('skillAdmin.totalCol')}{sortIndicator('total_references')}
                </th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">
                  {t('skillAdmin.actionsCol')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {skills.map((skill) => (
                <tr
                  key={skill.id}
                  className={selected.has(skill.id) ? 'bg-brand-50' : ''}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(skill.id)}
                      onChange={() => toggleSelect(skill.id)}
                      aria-label={`Select ${skill.name}`}
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {editingId === skill.id && !skill.taxonomy_id ? (
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        ref={(el) => el?.focus()}
                        onBlur={saveEdit}
                        onKeyDown={handleEditKeyDown}
                        className="block w-full rounded-md border-gray-300 text-sm"
                      />
                    ) : (
                      skill.name
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    {editingId === skill.id && !skill.taxonomy_id ? (
                      <input
                        type="text"
                        value={editCategory}
                        onChange={(e) => setEditCategory(e.target.value)}
                        onBlur={saveEdit}
                        onKeyDown={handleEditKeyDown}
                        className="block w-full rounded-md border-gray-300 text-sm"
                      />
                    ) : (
                      <span className="flex items-center gap-1">
                        {skill.category ?? '\u2014'}
                        {skill.taxonomy_id && <span className="text-[10px] text-gray-400" title="Managed by taxonomy">T</span>}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {refBadge(skill.employee_count)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {refBadge(skill.project_report_count)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {refBadge(skill.tender_count)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {refBadge(skill.total_references)}
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {editingId === skill.id ? (
                      <>
                        <button
                          type="button"
                          onClick={saveEdit}
                          className="text-xs font-medium text-brand-600 hover:text-brand-800"
                        >
                          {t('skillAdmin.save')}
                        </button>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          {t('skillAdmin.cancel')}
                        </button>
                      </>
                    ) : (
                      <>
                        {!skill.taxonomy_id && (
                        <button
                          type="button"
                          onClick={() => startEdit(skill)}
                          className="text-xs font-medium text-brand-600 hover:text-brand-800"
                        >
                          {t('skillAdmin.edit')}
                        </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDelete(skill)}
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          {t('skillAdmin.delete')}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <nav
          aria-label="Skill list pagination"
          className="flex items-center justify-between border-t border-gray-200 pt-4"
        >
          <p className="text-sm text-gray-500">
            {t('common.pageOf', { page, totalPages })}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {t('common.previous')}
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {t('common.next')}
            </button>
          </div>
        </nav>
      )}
    </div>
  );
}
